import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiClipboardCopy, HiCheck, HiPlay, HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { IoRadioButtonOn } from 'react-icons/io5';
import { useAuth } from '../contexts/AuthContext';
import { useAuthHeaders } from '../hooks/useAuthHeaders';
import './admin/NCAdmin.css';
import './MyStreams.css';

export default function MyStreams({ config }) {
  const { user } = useAuth();
  const { getAuthHeaders } = useAuthHeaders();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState({});
  const [ending, setEnding] = useState({});
  const [copied, setCopied] = useState({});
  const [pastOpen, setPastOpen] = useState(false);

  useEffect(() => { if (user) loadMyGames(); }, [user]);

  const loadMyGames = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/games`);
      const data = await res.json();
      setGames((data.data.games || [])
        .filter(g => g.streamerId === user.sub)
        .sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart)));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const startStream = async (gameId) => {
    setStarting(s => ({ ...s, [gameId]: true }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${config.apiUrl}/games/${gameId}/start`, { method: 'POST', headers });
      if (res.ok) await loadMyGames();
      else { const err = await res.json(); alert(err.message || 'Failed to start stream'); }
    } catch (e) { console.error(e); }
    finally { setStarting(s => ({ ...s, [gameId]: false })); }
  };

  const endGame = async (gameId) => {
    setEnding(s => ({ ...s, [gameId]: true }));
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${config.apiUrl}/games/${gameId}/stop`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ end: true }),
      });
      if (res.ok) await loadMyGames();
      else { const err = await res.json(); alert(err.message || 'Failed to end game'); }
    } catch (e) { console.error(e); }
    finally { setEnding(s => ({ ...s, [gameId]: false })); }
  };

  const copy = (key, text) => {
    setCopied(c => ({ ...c, [key]: true }));
    setTimeout(() => setCopied(c => ({ ...c, [key]: false })), 2000);
  };

  const formatDate = (d) => new Date(d).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  if (loading) return (
    <div className="nc-admin" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="loading-spinner" />
    </div>
  );

  const liveGames = games.filter(g => g.status === 'live');
  const futureGames = games.filter(g => ['scheduled', 'ready'].includes(g.status));
  const upNext = futureGames[0] || null;
  const upcoming = futureGames.slice(1);
  const pastGames = games.filter(g => ['ended', 'recorded'].includes(g.status))
    .sort((a, b) => new Date(b.scheduledStart) - new Date(a.scheduledStart));

  const hasAnything = liveGames.length || futureGames.length || pastGames.length;

  const GameCard = ({ game, compact = false }) => (
    <div className={`nc-mystreams-card ${compact ? 'compact' : ''}`}>
      <div className="nc-mystreams-card-header">
        <div className="nc-mystreams-card-left">
          <span className={`nc-admin-status ${game.status}`}>
            {game.status === 'live' && <IoRadioButtonOn style={{ marginRight: 4 }} />}
            {game.status}
          </span>
          <span className="nc-admin-game-sport">{game.sport}</span>
          <span className="nc-mystreams-teams">{game.homeTeam} vs {game.awayTeam}</span>
        </div>
        <div className="nc-admin-game-meta">
          {game.venue}{game.field ? ` — ${game.field}` : ''}<br />
          {formatDate(game.scheduledStart)}
        </div>
      </div>

      {!compact && game.streamKey && (
        <div className="nc-mystreams-details">
          <div className="nc-mystreams-field">
            <span className="nc-mystreams-label">Stream URL</span>
            <div className="nc-mystreams-value">
              <button className="nc-mystreams-copy" onClick={() => copy(`ingest-${game.gameId}`, `rtmps://${game.ingestEndpoint}/app`)}>
                {copied[`ingest-${game.gameId}`] ? <HiCheck /> : <HiClipboardCopy />}
              </button>
              <code>rtmps://{game.ingestEndpoint}/app</code>
            </div>
          </div>
          <div className="nc-mystreams-field">
            <span className="nc-mystreams-label">Stream Key</span>
            <div className="nc-mystreams-value">
              <button className="nc-mystreams-copy" onClick={() => copy(`key-${game.gameId}`, game.streamKey)}>
                {copied[`key-${game.gameId}`] ? <HiCheck /> : <HiClipboardCopy />}
              </button>
              <code>{game.streamKey}</code>
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <Link to={`/stream/${game.gameId}/browser`} className="nc-admin-btn primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}>
              ● Go Live in Browser
            </Link>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <button className="nc-admin-btn" onClick={() => endGame(game.gameId)} disabled={ending[game.gameId]}
              style={{ background: '#ef4444', color: '#fff' }}>
              {ending[game.gameId]
                ? <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Ending...</>
                : '■ End Game'}
            </button>
          </div>
        </div>
      )}

      {!compact && !game.streamKey && game.status === 'scheduled' && (
        <div className="nc-mystreams-pending">
          <span>Channel not ready yet — will be created 15 min before kick-off</span>
          <button className="nc-admin-btn" onClick={() => startStream(game.gameId)} disabled={starting[game.gameId]}>
            {starting[game.gameId]
              ? <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} /> Starting...</>
              : <><HiPlay /> Start Now</>}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="nc-admin">
      <div className="nc-admin-eyebrow">NC LIVE</div>
      <h1 className="nc-admin-title" style={{ marginBottom: '3rem' }}>My Streams</h1>

      {!hasAnything ? (
        <div className="nc-admin-empty">No games assigned yet — an admin will assign games to you</div>
      ) : (
        <div className="nc-mystreams-sections">

          {liveGames.length > 0 && (
            <section className="nc-mystreams-section">
              <h2 className="nc-mystreams-section-title live">Live</h2>
              {liveGames.map(g => <GameCard key={g.gameId} game={g} />)}
            </section>
          )}

          {upNext && (
            <section className="nc-mystreams-section">
              <h2 className="nc-mystreams-section-title">Up Next</h2>
              <GameCard game={upNext} />
            </section>
          )}

          {upcoming.length > 0 && (
            <section className="nc-mystreams-section">
              <h2 className="nc-mystreams-section-title">Upcoming</h2>
              {upcoming.map(g => <GameCard key={g.gameId} game={g} />)}
            </section>
          )}

          {pastGames.length > 0 && (
            <section className="nc-mystreams-section">
              <button className="nc-mystreams-past-toggle" onClick={() => setPastOpen(o => !o)}>
                <h2 className="nc-mystreams-section-title muted">Past ({pastGames.length})</h2>
                {pastOpen ? <HiChevronUp /> : <HiChevronDown />}
              </button>
              {pastOpen && pastGames.map(g => <GameCard key={g.gameId} game={g} compact />)}
            </section>
          )}

        </div>
      )}
    </div>
  );
}
